import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ENV_CONFIG } from '@ps/core-config';
import type { Nullable } from '@ps/utils';
import {
  BillingRulesPayloadDto,
  BillingRulesResponseDto,
  BuyerSettingsResponseDto,
  BuyerSettingsUpdatePayloadDto,
  DecipherHashKeyIdsResponseDto,
} from './dtos/buyer.dto';
import { BuyerSettingsMapper } from './mappers/buyer.mapper';
import { BillingRulesMapper } from './mappers/billing-rules.mapper';
import { BillingRules, BuyerSettings } from '../state/buyer-state.model';
import { withLoading } from '@ps/core-overlay';

@Injectable({ providedIn: 'root' })
export class BuyerApi {
  private readonly _http = inject(HttpClient);
  private readonly _envConfig = inject(ENV_CONFIG);
  private readonly _mapper = new BuyerSettingsMapper();
  private readonly _billingRulesMapper = new BillingRulesMapper();

  private readonly _baseUrl = `${this._envConfig.pureSpectrum.url}`;

  public getByBuyerId(buyerId: number): Observable<BuyerSettings> {
    return this._http
      .get<BuyerSettingsResponseDto>(
        `${this._baseUrl}/buyerSettings/${buyerId}`,
        {
          context: withLoading('page'),
        },
      )
      .pipe(map((response) => this._mapper.fromDto(response.buyer)));
  }

  public update(
    cmp: number,
    dto: BuyerSettingsUpdatePayloadDto,
  ): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/buyerSettings/${cmp}`, dto);
  }

  public getDecipherHashKeyIds(): Observable<number[]> {
    return this._http
      .get<DecipherHashKeyIdsResponseDto>(`${this._baseUrl}/decipherHashKeyIds`)
      .pipe(map((response) => response.data.map((item) => item.id)));
  }

  public getBillingRules(): Observable<Nullable<BillingRules>> {
    return this._http
      .get<BillingRulesResponseDto>(
        `${this._baseUrl}/buyers/v2/billing-rules`,
        {
          context: withLoading('page'),
        },
      )
      .pipe(
        map((response) => {
          if ('ps_api_response_code' in response) {
            return null;
          }
          return this._billingRulesMapper.fromDto(response);
        }),
      );
  }

  public createBillingRules(
    payload: BillingRulesPayloadDto,
  ): Observable<BillingRules> {
    return this._http
      .post<BillingRulesResponseDto>(
        `${this._baseUrl}/buyers/v2/billing-rules`,
        payload,
        {
          context: withLoading('page'),
        },
      )
      .pipe(
        map((response) => {
          if ('ps_api_response_code' in response) {
            throw new Error('Failed to create billing rules');
          }
          return this._billingRulesMapper.fromDto(response);
        }),
      );
  }

  public updateBillingRules(
    id: string,
    payload: BillingRulesPayloadDto,
  ): Observable<BillingRules> {
    return this._http
      .put<BillingRulesResponseDto>(
        `${this._baseUrl}/buyers/v2/billing-rules/${id}`,
        payload,
        {
          context: withLoading('page'),
        },
      )
      .pipe(
        map((response) => {
          if ('ps_api_response_code' in response) {
            throw new Error('Failed to update billing rules');
          }
          return this._billingRulesMapper.fromDto(response);
        }),
      );
  }
}
